/**
 * Wraps a real app component so it reads as a "screenshot" inside a
 * help-centre article. The wrapper:
 *
 *   - Marks the subtree `inert` so nothing inside reacts to clicks,
 *     focus, or keyboard input. Buttons render but don't navigate.
 *   - Adds a soft frame (border + shadow + bg) and an optional caption
 *     so the embed is visually distinct from surrounding article text.
 *   - Optionally simulates browser chrome (a fake URL bar) for full-
 *     page surfaces where the URL itself is part of the explanation.
 *
 * Because it embeds the *real* app component, the visual stays in sync
 * with whatever the app actually looks like — no PNGs to refresh, no
 * mock primitives to drift from reality.
 *
 * Props:
 *   - children: the real component(s) to render
 *   - caption:  short italic line shown below the frame (optional)
 *   - browser:  if set, a string URL shown in a fake address bar
 *   - className: extra classes on the outer figure
 *   - padded:   boolean, default true — pad the inner area; set false
 *               for components that bring their own card chrome.
 */
export default function Screenshot({
  children,
  caption,
  browser,
  className = "",
  padded = true,
}) {
  return (
    <figure className={`my-2 ${className}`.trim()}>
      <div
        // `inert` is the modern attribute for fully neutralizing a
        // subtree — no clicks, no focus, no keyboard. React passes
        // unknown attributes through to the DOM, so this works in
        // 19+ without any special handling.
        inert={true}
        // Belt and suspenders for older browsers — the inert
        // attribute already does this, but pointer-events keeps the
        // mouse from showing interactive cursors.
        className="pointer-events-none select-none rounded-xl border border-border bg-card shadow-sm overflow-hidden"
        aria-hidden="true"
      >
        {browser && <FakeBrowserChrome url={browser} />}
        <div className={padded ? "p-4 md:p-5" : ""}>{children}</div>
      </div>
      {caption && (
        <figcaption className="mt-2 text-xs text-muted-foreground italic text-center">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function FakeBrowserChrome({ url }) {
  return (
    <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-3 py-2">
      <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
      <div className="ml-3 flex-1 rounded-md bg-card border border-border px-2 py-1 text-[10px] text-muted-foreground/80 truncate">
        {url}
      </div>
    </div>
  );
}
